import React, { useState, useEffect } from 'react';
import Logo from '../../images/logo.png'
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link as RouterLink } from 'react-router-dom';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import AuthLayout, { authFieldSx } from './AuthLayout';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setLoading(false);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSubmitting(true);

    const user = {
      email: email,
      password: password,
    };

    try {
      // Timeout-AbortController für Request-Timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 Sekunden Timeout

      const response = await fetch(`${getApiBaseUrl()}v2/authentication/register/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(user),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Überprüfe HTTP-Status
      if (!response.ok) {
        // Versuche Fehlermeldung aus Response zu extrahieren
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          throw new Error(`Server-Fehler: ${response.status} ${response.statusText}`);
        }

        // Spezifische Fehlermeldungen basierend auf Status-Code
        if (response.status === 400) {
          setErrorMessage('Ungültige Eingabedaten. Bitte überprüfen Sie E-Mail und Passwort.');
        } else if (response.status === 409) {
          setErrorMessage('Diese E-Mail-Adresse ist bereits registriert.');
        } else if (response.status >= 500) {
          setErrorMessage('Server-Fehler. Bitte versuchen Sie es später erneut.');
        } else if (errorData.message || errorData.error) {
          setErrorMessage(errorData.message || errorData.error || 'Ein Fehler ist aufgetreten.');
        } else {
          setErrorMessage('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
        }
        
        setPassword('');
        setSubmitting(false);
        return;
      }

      // Parse JSON-Response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Ungültige Antwort vom Server.');
      }

      // Überprüfe Erfolg
      if (data.status === 'success' || data.key || data.success) {
        window.location.replace('/');
      } else {
        const statusMessages = {
          invalidMail: 'Ungültige E-Mail-Adresse.',
          invalidPassword: 'Ungültiges Passwort.',
          alreadyExists: 'Diese E-Mail-Adresse ist bereits registriert. Bitte anmelden.',
          toManyRequests: 'Zu viele Anfragen. Bitte später erneut versuchen.',
        };
        setErrorMessage(
          data.message ||
          statusMessages[data.status] ||
          'Es kann mit den gegebenen Daten kein Account erstellt werden.'
        );
        setPassword('');
        setSubmitting(false);
      }
    } catch (error) {
      // Netzwerkfehler und andere Fehler behandeln
      if (error.name === 'AbortError') {
        setErrorMessage('Die Anfrage hat zu lange gedauert. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.');
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setErrorMessage('Keine Verbindung zum Server möglich. Bitte überprüfen Sie Ihre Internetverbindung.');
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
      
      setPassword('');
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      logo={Logo}
      logoAlt="FireCore"
      title={loading ? null : 'Konto erstellen'}
      subtitle={loading ? null : 'Registrieren Sie sich, um FireCore zu nutzen.'}
      footer={
        !loading && (
          <p className="authenticate-switch">
            Bereits ein Konto?
            <RouterLink to="/auth/login">Anmelden</RouterLink>
          </p>
        )
      }
    >
      {errorMessage && <div className="authenticate-error">{errorMessage}</div>}
      {loading === false && (
        <form className="authenticate-form" onSubmit={onSubmit}>
          <TextField
            id="email"
            label="E-Mail-Adresse"
            type="email"
            autoComplete="email"
            autoFocus
            fullWidth
            variant="outlined"
            sx={authFieldSx}
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setErrorMessage('');
            }}
            disabled={submitting}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon fontSize="medium" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            id="password"
            label="Passwort"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            fullWidth
            variant="outlined"
            sx={authFieldSx}
            value={password}
            onChange={e => {
              setPassword(e.target.value);
              setErrorMessage('');
            }}
            disabled={submitting}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="medium" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="medium" /> : <Visibility fontSize="medium" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <p className="authenticate-hint">
            Beim Registrieren wird automatisch eine neue Firma erstellt. Sie erhalten alle Rechte.
          </p>
          <Button
            type="submit"
            variant="contained"
            className="authenticate-submit"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={22} color="inherit" /> : 'Registrieren'}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
};

export default Signup;
